import { useState, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";

import { timetableService, type TimetablePeriod, type TimetableRequest } from "../../services/timetableService";
import { apiErrorMessage } from "../../utils/apiError";
import AIAssistField from "../AIAssistField";
import { Field } from "../FormLayout";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { requestConfig, requestStatus, requestTypeLabel, requestedChangeText } from "./timetableLogic";

const asked = (row: TimetableRequest) => row.created_at?.slice(0, 10) ?? "";

/**
 * Timetable change requests. Teachers see their own; the Director of Studies
 * and leadership decide them and then make the change -- two steps, because
 * approving says yes and making it moves the lesson, checked for clashes.
 */
export function RequestsPanel({ rows, periods, isLoading, isError, onRetry, canDecide, onChanged, actions, title, description }: {
  rows: TimetableRequest[] | undefined;
  periods: Map<number, TimetablePeriod>;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canDecide: boolean;
  onChanged: () => void;
  actions?: ReactNode;
  title: string;
  description: string;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [notes, setNotes] = useState("");
  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: number; decision: "approved" | "rejected" }) => timetableService.reviewRequest(id, decision, notes.trim()),
    onSuccess: (_result, variables) => {
      setNotes("");
      setNotice({ tone: "success", text: variables.decision === "approved" ? "Request approved. Make the change when you are ready." : "Request declined; the teacher sees why." });
      onChanged();
    },
  });
  const implement = useMutation({
    mutationFn: timetableService.implementRequest,
    onSuccess: () => {
      setViewing(null);
      setNotice({ tone: "success", text: "The change is made; the lesson has moved in everyone's timetable." });
      onChanged();
    },
  });

  const columns: RegisterColumn<TimetableRequest>[] = [
    { header: "Asked", cell: asked, exportValue: asked, className: "whitespace-nowrap tabular-nums text-gray-700" },
    ...(canDecide ? [{ header: "By", cell: (row: TimetableRequest) => <span className="font-medium text-gray-900">{row.requested_by_name}</span>, exportValue: (row: TimetableRequest) => row.requested_by_name }] : []),
    { header: "Lesson", cell: (row) => row.current_slot_display, exportValue: (row) => row.current_slot_display, className: "min-w-[14rem] whitespace-normal text-gray-700" },
    { header: "Change", cell: (row) => requestTypeLabel(row.request_type), exportValue: (row) => requestTypeLabel(row.request_type), className: "text-gray-700" },
    { header: "Asked for", cell: (row) => requestedChangeText(row, periods), exportValue: (row) => requestedChangeText(row, periods), className: "min-w-[10rem] whitespace-normal text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot {...requestStatus(row)} />, exportValue: (row) => requestStatus(row).label },
  ];

  const request = viewing !== null ? rows?.find((row) => row.id === viewing) : undefined;
  const typed = notes.trim();

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title={title}
        description={description}
        actions={actions}
        rows={rows}
        config={requestConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          setNotes("");
          decide.reset();
          implement.reset();
          setViewing(row.id);
        }}
        openLabel={(row) => (canDecide && (row.status === "pending" || row.status === "approved") ? "Decide" : "Open")}
        noun={["request", "requests"]}
        exportName={{ file: "timetable-change-requests", title: "Timetable Change Requests" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canDecide ? "No teacher has asked for a timetable change." : "You have not asked for a timetable change."}
      />

      {request && (
        <DetailsModal
          title={requestTypeLabel(request.request_type)}
          headline={{ label: "Lesson", value: request.current_slot_display, note: `Asked for: ${requestedChangeText(request, periods)}` }}
          status={requestStatus(request)}
          facts={[
            ["Asked by", request.requested_by_name],
            ["Asked on", asked(request)],
            ["Change", requestTypeLabel(request.request_type)],
            ["Asked for", requestedChangeText(request, periods)],
            ["Decided by", request.reviewed_by_name],
            ["Academic year", request.academic_year_name],
          ]}
          sections={[
            { title: "Why it is needed", body: request.reason },
            { title: request.status === "rejected" ? "Why it was declined" : "Review notes", body: request.review_notes, tone: request.status === "rejected" ? "danger" : "plain" },
          ]}
          onClose={() => setViewing(null)}
        >
          {canDecide && request.status === "pending" && (
            <ActionPanel title="Decide" description="Approving says yes; you then make the change, which is checked for clashes. Declining needs a reason the teacher will read.">
              <Field as="div" label="Note to the teacher" hint="Needed to decline (at least 5 characters).">
                <AIAssistField
                  rows={2}
                  value={notes}
                  onChange={(next) => setNotes(next)}
                  placeholder="e.g. The lab is booked for S4 practicals then; try Thursday period 3."
                  context={{ key: "timetable.request_note", params: { purpose: typed ? "reject" : "approve", request: request.id } }}
                  draftLabel="Draft a note"
                  draftHint="Reads the request. Jot your reason first to decline, and it writes it up."
                />
              </Field>
              {decide.isError && <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{apiErrorMessage(decide.error, "The decision could not be saved.")}</p>}
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  disabled={typed.length < 5 || decide.isPending}
                  title={typed.length < 5 ? "Write why before declining" : undefined}
                  onClick={() => decide.mutate({ id: request.id, decision: "rejected" })}
                  className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  Decline
                </button>
                <button type="button" disabled={decide.isPending} onClick={() => decide.mutate({ id: request.id, decision: "approved" })} className="btn btn-primary">
                  Approve
                </button>
              </div>
            </ActionPanel>
          )}
          {canDecide && request.status === "approved" && (
            <ActionPanel title="Make the change" description="Moves the lesson now. It is refused if it would book a class or teacher twice.">
              {implement.isError && <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{apiErrorMessage(implement.error, "The change could not be made.")}</p>}
              <div className="flex justify-end">
                <button type="button" disabled={implement.isPending} onClick={() => implement.mutate(request.id)} className="btn btn-primary">
                  {implement.isPending ? "Making it…" : "Make the change"}
                </button>
              </div>
            </ActionPanel>
          )}
        </DetailsModal>
      )}
    </div>
  );
}
